import React, { useMemo, useState } from 'react';
import PageHeader from '../../components/shared/PageHeader';
import DataToolbar, { StatusPill, Card } from '../../components/shared/DataToolbar';
import { LoadingState, ErrorState, EmptyResults } from '../../components/shared/Pending';
import { Home, ExternalLink, Star } from 'lucide-react';
import { useApi } from '../../api/useApi';
import { listAdminProperties } from '../../api/adminApi';

const FRONTEND_URL = (process.env.REACT_APP_FRONTEND_URL || 'https://stayeasy.io').replace(/\/$/, '');
const imageUrl = (property) => property?.cover_image?.url || property?.cover_image || 'https://placehold.co/600x360';

export default function AdminProperties() {
  const { data, loading, error, refetch } = useApi(listAdminProperties, []);
  const properties = useMemo(() => Array.isArray(data) ? data : (data?.items || data || []), [data]);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');

  const filtered = properties.filter((property) => {
    const haystack = `${property.title || ''} ${property.location || ''} ${property.host?.name || ''}`.toLowerCase();
    const matchesQuery = !q || haystack.includes(q.toLowerCase());
    const matchesStatus = !status || (property.status || '').toLowerCase() === status.toLowerCase();
    return matchesQuery && matchesStatus;
  });

  return (
    <div>
      <PageHeader title="Properties" description="All property listings across StayEasy." />
      <DataToolbar
        search={q}
        onSearch={setQ}
        placeholder="Search property, location or host…"
        filters={[{ label: 'Status', value: status, options: ['All', 'Published', 'Draft', 'Archived'], onChange: setStatus }]}
      />

      {loading ? <LoadingState label="Loading properties…" />
        : error ? <ErrorState error={error} onRetry={refetch} />
        : filtered.length === 0 ? <EmptyResults icon={Home} title="No properties found" description="No marketplace properties match these filters." />
        : (
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-5">
            {filtered.map((property) => (
              <Card key={property.id} className="overflow-hidden">
                <div className="aspect-[16/10] overflow-hidden relative bg-slate-100">
                  <img src={imageUrl(property)} alt="" className="h-full w-full object-cover" />
                  <div className="absolute top-3 left-3"><StatusPill status={property.status} /></div>
                </div>
                <div className="p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <h3 className="font-semibold text-slate-900 truncate">{property.title}</h3>
                      <p className="text-xs text-slate-500 truncate mt-0.5">{property.location || 'Location not set'}</p>
                    </div>
                    {property.slug && (
                      <a
                        href={`${FRONTEND_URL}/properties/${property.slug}`}
                        target="_blank"
                        rel="noreferrer"
                        className="h-8 w-8 shrink-0 grid place-items-center rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-900"
                        title="Open public listing"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    )}
                  </div>

                  <div className="mt-4 rounded-lg bg-slate-50 p-3">
                    <div className="text-[11px] uppercase tracking-wider text-slate-400">Host</div>
                    <div className="mt-1 text-sm font-medium text-slate-900">{property.host?.name || 'Unknown host'}</div>
                  </div>

                  <div className="mt-4 flex items-center justify-between">
                    <div className="text-sm"><span className="font-bold text-slate-900 tabular-nums">${property.price_per_night ?? 0}</span><span className="text-slate-500 text-xs"> /night</span></div>
                    <div className="flex items-center gap-1 text-xs text-slate-500"><Star className="h-3.5 w-3.5 text-amber-400 fill-amber-400" /> {property.average_rating ?? '—'} <span className="text-slate-400">({property.review_count ?? 0})</span></div>
                  </div>

                  <div className="mt-3 grid grid-cols-3 gap-2 text-center text-xs">
                    <div className="rounded-lg bg-slate-50 py-1.5"><div className="font-semibold text-slate-900">{property.beds ?? '—'}</div><div className="text-slate-500">Beds</div></div>
                    <div className="rounded-lg bg-slate-50 py-1.5"><div className="font-semibold text-slate-900">{property.baths ?? '—'}</div><div className="text-slate-500">Baths</div></div>
                    <div className="rounded-lg bg-slate-50 py-1.5"><div className="font-semibold text-slate-900">{property.max_guests ?? '—'}</div><div className="text-slate-500">Guests</div></div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
    </div>
  );
}
